import { container } from '@eureka/services/src/container';
import type { ICancelSubscriptionRequest, ICreatePaymentSessionRequest, ICreatePaymentSessionResponse, IGetInvoicesRequest, IResumeSubscriptionRequest, ISubscriptionListDTO, IStripeSubscription, IUpgradeSubscriptionRequest, IUpgradeSubscriptionResponse, IDowngradeSubscriptionRequest, ICancelScheduledDowngradeRequest, IPersonalCreditsDetailResponse } from '../types';
import type { GetRechargeEligibilityResponse } from '@/domains/credits/types';

export interface IGetCheckSessionData {
  token: string;
  refresh_token: string;
}

export function createPaymentSession(params: ICreatePaymentSessionRequest): Promise<ICreatePaymentSessionResponse> {
    return container.RnDHttp.post<ICreatePaymentSessionResponse>({
        url: `${import.meta.env.VITE_ACCOUNT_SERVICE_URI}/user-service/payment/stripe/checkout-session`,
        data: params,
    })
}

export function cancelSubscription(params: ICancelSubscriptionRequest): Promise<void> {
    return container.AccountHttp.post({
        url: `${import.meta.env.VITE_ACCOUNT_SERVICE_URI}/user-service/payment/stripe/cancel-subscription`,
        data: params,
    }).then(res => {
        return res
    })
}

export function resumeSubscription(params: IResumeSubscriptionRequest): Promise<void> {
    return container.AccountHttp.post({
        url: `${import.meta.env.VITE_ACCOUNT_SERVICE_URI}/user-service/payment/stripe/resume-subscription/${params.subscription_id}`,
        data: params,
    }).then(res => {
        return res
    })
}

export function getInvoices(params: IGetInvoicesRequest): Promise<string> {
    return container.AccountHttp.post({
        url: `${import.meta.env.VITE_ACCOUNT_SERVICE_URI}/user-service/payment/stripe/create-portal-session`,
        data: params,
    }).then(res => {
        return res
    })
}

export function getSubscriptionList(): Promise<ISubscriptionListDTO[]> {
    return container.AccountHttp.get({
        url: `${import.meta.env.VITE_ACCOUNT_SERVICE_URI}/user-service/payment/tenant-user/subscriptions`,
    }).then(res => {
        return res
    })
}

export function checkPaymentStatus(timestamp: string): Promise<boolean> {
    return container.AccountHttp.get({
        url: `${import.meta.env.VITE_ACCOUNT_SERVICE_URI}/user-service/payment/success?timestamp=${timestamp}`,
    }).then(res => {
        return res
    })
}

/**
 * 获取 Stripe 实时订阅信息
 * 从 Stripe 实时查询当前登录用户的订阅信息，包含 subscription schedule 信息
 */
export function getStripeSubscriptions(): Promise<IStripeSubscription[]> {
    return container.AccountHttp.get({
        url: `${import.meta.env.VITE_ACCOUNT_SERVICE_URI}/user-service/payment/stripe/subscriptions`,
    }).then(res => {
        return res || []
    })
}

/**
     * 检查积分充值资格
     * GET /api/eureka/credits/personal/recharge-info
     */
export function getRechargeEligibility(): Promise<GetRechargeEligibilityResponse> {
    return container.AccountHttp.get<GetRechargeEligibilityResponse>({
        url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/api/eureka/credits/personal/recharge-info`,
    }).then(res => {
        return res as GetRechargeEligibilityResponse;
    })
}

/**
 * 升级订阅
 * 立即升级时会从升级时间开始新的订阅周期，不退款不抵扣旧周期
 * 如果自动扣款失败，返回 hosted_invoice_url 供用户手动支付
 */
export function upgradeSubscription(params: IUpgradeSubscriptionRequest): Promise<IUpgradeSubscriptionResponse> {
    return container.AccountHttp.post<IUpgradeSubscriptionResponse>({
        url: `${import.meta.env.VITE_ACCOUNT_SERVICE_URI}/user-service/payment/stripe/upgrade-subscription`,
        data: params,
    })
}

/**
 * 降级订阅
 * 默认在当前周期结束后生效，会创建或更新 subscription schedule
 * 如果传入 effective_type=immediate，会立即生效
 */
export function downgradeSubscription(params: IDowngradeSubscriptionRequest): Promise<void> {
    return container.AccountHttp.post({
        url: `${import.meta.env.VITE_ACCOUNT_SERVICE_URI}/user-service/payment/stripe/downgrade-subscription`,
        data: params,
    })
}

/**
 * 取消预约降级
 * 取消已创建的 subscription schedule，恢复到当前套餐
 */
export function cancelScheduledDowngrade(params: ICancelScheduledDowngradeRequest): Promise<void> {
    return container.AccountHttp.post({
        url: `${import.meta.env.VITE_ACCOUNT_SERVICE_URI}/user-service/payment/stripe/resume-subscription/${params.subscription_id}`,
    })
}

/**
 * 获取个人积分详情
 * GET /api/eureka/credits/personal/credits-detail
 */
export function getPersonalCreditsDetail(): Promise<IPersonalCreditsDetailResponse> {
    return container.AccountHttp.get<IPersonalCreditsDetailResponse>({
        url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/api/eureka/credits/personal/detail`,
    }).then(res => {
        return res as IPersonalCreditsDetailResponse;
    })
}
